import type { CSSProperties } from 'react';
import type { PatternSlideBackground, SlideLayoutTemplate, SlidePattern } from '../../types/api';
import type { TemplateBox } from './slideTemplates';

/**
 * Picture sources beyond the image model, as much of them as CSS can say
 * (IMAGE_VARIETY_PLAN §14 S2, S3, S6): the pattern grounds a type-only slide
 * sits on, and where an icon glyph lands.
 *
 * Mirrors of the backend render constants (app/rendering/render.py and
 * app/rendering/spec.py): canvas 1080, PATTERN_SPACING 48, PATTERN_INK_ALPHA
 * about 22%, and the ICON_PLACEMENT table. Change one side, change the other.
 *
 * Every geometry number is a fraction of the square slide, like
 * `slideTemplates.ts`, so the stage can be any size and still mean what the
 * PNG means. The pattern lengths are emitted in `cqw` (the stage is an
 * inline-size container), which is the same fraction of the canvas edge. The
 * server PNG stays the truth: the browser's anti-aliasing and the tile's
 * phase against the canvas edge are not the renderer's.
 */

/** The renderer's canvas edge. Every geometry number below is a fraction of it. */
const CANVAS_SIZE = 1080;

function ofCanvas(pixels: number): number {
  return Math.round((pixels / CANVAS_SIZE) * 10000) / 10000;
}

/** Turns a canvas fraction into a CSS length. */
export type CanvasLength = (fraction: number) => string;

/** A canvas fraction as a container-query length: 1 = the whole stage width. */
export const cqw: CanvasLength = (fraction) => `${Math.round(fraction * 100 * 1000) / 1000}cqw`;

/**
 * A canvas fraction in pixels for a canvas `size` px wide: for a swatch chip,
 * which sits outside the stage's container and previews the tile larger than
 * the slide would, so a 28px chip still shows the pattern.
 */
export function pixelsOf(size: number): CanvasLength {
  return (fraction) => `${Math.round(fraction * size * 100) / 100}px`;
}

/** The four patterns, in the backend's `PATTERNS` order. */
export const SLIDE_PATTERNS: readonly SlidePattern[] = ['dots', 'grid', 'diagonal', 'waves'];

/** Words for the patterns. A `Record`, so a new arm is a compile error until it has one. */
export const SLIDE_PATTERN_LABELS: Readonly<Record<SlidePattern, string>> = {
  dots: 'Dots',
  grid: 'Grid',
  diagonal: 'Diagonal',
  waves: 'Waves',
};

/** Distance between repeats: dot centres, grid lines, diagonal lines, wave rows (48/1080). */
export const PATTERN_SPACING = ofCanvas(48);

/** How strongly the ink shows over the ground (about 22%), so type stays legible. */
export const PATTERN_INK_ALPHA = 0.22;

/** A dot's radius (5/1080). */
export const PATTERN_DOT_RADIUS = ofCanvas(5);

/** Stroke width of grid, diagonal and wave lines (2/1080). */
export const PATTERN_LINE_WIDTH = ofCanvas(2);

/** A wave's peak height either side of its row's centre line (8/1080). */
export const PATTERN_WAVE_AMPLITUDE = ofCanvas(8);

/** One wave's length along the row: two spacings (96/1080). */
export const PATTERN_WAVE_LENGTH = ofCanvas(96);

/** The ink at the pattern's opacity, as a CSS colour. */
function patternInk(ink: string): string {
  return `color-mix(in srgb, ${ink} ${Math.round(PATTERN_INK_ALPHA * 100)}%, transparent)`;
}

/**
 * The waves tile as an inline SVG: one wavelength wide, one spacing tall, a
 * quadratic sine through the middle. Drawn in tile units (the tile is
 * 96 x 48 in render pixels) and scaled by `background-size`.
 */
function wavesTile(ink: string): string {
  const width = Math.round(PATTERN_WAVE_LENGTH * CANVAS_SIZE);
  const height = Math.round(PATTERN_SPACING * CANVAS_SIZE);
  const mid = height / 2;
  // A quadratic's peak sits halfway to its control point, so the control
  // point is twice the amplitude off the centre line.
  const control = Math.round(PATTERN_WAVE_AMPLITUDE * CANVAS_SIZE) * 2;
  const stroke = Math.round(PATTERN_LINE_WIDTH * CANVAS_SIZE);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" ` +
    `viewBox="0 0 ${width} ${height}" preserveAspectRatio="none">` +
    `<path d="M0 ${mid} Q${width / 4} ${mid - control} ${width / 2} ${mid} ` +
    `T${width} ${mid}" fill="none" stroke="${ink}" stroke-opacity="${PATTERN_INK_ALPHA}" ` +
    `stroke-width="${stroke}"/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/**
 * The paint for a pattern background: the ground fill, then the pattern in
 * the ink at low opacity, over the whole canvas whatever the template (there
 * is no picture to slot, exactly as with a gradient).
 */
export function patternBackgroundCss(
  background: Pick<PatternSlideBackground, 'pattern' | 'ground' | 'ink'>,
  length: CanvasLength = cqw,
): CSSProperties {
  const { ground, ink } = background;
  const tint = patternInk(ink);
  const spacing = length(PATTERN_SPACING);
  const line = length(PATTERN_LINE_WIDTH);
  // Tiles start at the canvas's top-left, as the renderer draws them; the
  // stage's own `cover` / `center` defaults are for pictures.
  const base: CSSProperties = { backgroundColor: ground, backgroundPosition: '0 0' };
  switch (background.pattern) {
    case 'dots': {
      const radius = length(PATTERN_DOT_RADIUS);
      const edge = length(PATTERN_DOT_RADIUS + ofCanvas(1));
      return {
        ...base,
        backgroundImage: `radial-gradient(circle at center, ${tint} ${radius}, transparent ${edge})`,
        backgroundSize: `${spacing} ${spacing}`,
      };
    }
    case 'grid':
      return {
        ...base,
        backgroundImage:
          `linear-gradient(to right, ${tint} ${line}, transparent ${line}), ` +
          `linear-gradient(to bottom, ${tint} ${line}, transparent ${line})`,
        backgroundSize: `${spacing} ${spacing}`,
      };
    case 'diagonal':
      // Lines rising left to right ("/"), one spacing apart measured across them.
      return {
        ...base,
        backgroundImage: `repeating-linear-gradient(-45deg, ${tint} 0 ${line}, transparent ${line} ${spacing})`,
        backgroundSize: 'auto',
      };
    case 'waves':
      return {
        ...base,
        backgroundImage: wavesTile(ink),
        backgroundSize: `${length(PATTERN_WAVE_LENGTH)} ${spacing}`,
      };
  }
}

/** Narrows an untrusted value to a pattern name, or null. */
export function isSlidePattern(value: unknown): value is SlidePattern {
  return typeof value === 'string' && Object.hasOwn(SLIDE_PATTERN_LABELS, value);
}

/* ------------------------------------------------------------------ */
/* Icons                                                               */
/* ------------------------------------------------------------------ */

/** The renderer's safe margin (80/1080). */
const SAFE_MARGIN = 80;

/** A square icon box of `size` render pixels centred horizontally, its top at `top`. */
function centredBox(size: number, top: number): TemplateBox {
  return {
    left: ofCanvas((CANVAS_SIZE - size) / 2),
    top: ofCanvas(top),
    width: ofCanvas(size),
    height: ofCanvas(size),
  };
}

/**
 * A square icon centred in a picture slot at 60% of the slot's short side.
 * Slot geometry in render pixels, as `TEMPLATE_GEOMETRY` has it.
 */
function slotBox(left: number, top: number, width: number, height: number): TemplateBox {
  const size = Math.round(Math.min(width, height) * 0.6);
  return {
    left: ofCanvas(left + (width - size) / 2),
    top: ofCanvas(top + (height - size) / 2),
    width: ofCanvas(size),
    height: ofCanvas(size),
  };
}

/** Every template the table does not name: 200px, centred, at the top safe margin. */
const DEFAULT_ICON_BOX = centredBox(200, SAFE_MARGIN);

/**
 * Where the icon glyph sits, by the resolved template (S3's `ICON_PLACEMENT`):
 *
 * - `lower_third`: 340px, centred, top at the safe margin + 40 (upper area).
 * - `upper_third`: 340px, centred, bottom on the lower safe margin (lower area).
 * - `image_top` / `image_left` / `image_right`: centred in the slot at 60% of
 *   the slot's short side (top slot 1080 x 560, side slots 540 x 1080).
 * - every other template: {@link DEFAULT_ICON_BOX}.
 */
export const ICON_PLACEMENT: Readonly<Partial<Record<SlideLayoutTemplate, TemplateBox>>> = {
  lower_third: centredBox(340, SAFE_MARGIN + 40),
  upper_third: centredBox(340, CANVAS_SIZE - SAFE_MARGIN - 340),
  image_top: slotBox(0, 0, CANVAS_SIZE, 560),
  image_left: slotBox(0, 0, CANVAS_SIZE / 2, CANVAS_SIZE),
  image_right: slotBox(CANVAS_SIZE / 2, 0, CANVAS_SIZE / 2, CANVAS_SIZE),
};

/** The icon's box on the canvas for a template. */
export function iconBox(layoutTemplate: SlideLayoutTemplate): TemplateBox {
  return ICON_PLACEMENT[layoutTemplate] ?? DEFAULT_ICON_BOX;
}

/** An icon's glyph in the Lucide font, from its codepoint. */
export function iconGlyph(codepoint: number): string {
  return String.fromCodePoint(codepoint);
}
